/**
 * What to pay and how: Pay now (PayU), and, only while an admin has bank
 * transfer switched on, the bank details (UPI once a UPI ID is set) and the
 * form that tells us about a transfer. Used by the Payment page, the sign-up
 * success screen and the sign-in page when an account isn't active.
 */
import qrcode from "qrcode-generator";
import { Check, Copy } from "lucide-react";
import { useMemo, useState, type FormEvent } from "react";

import { Badge, Button, Field, Input, Select } from "@/components/ui";
import { track } from "@/lib/analytics";
import { formatINR } from "@/lib/format";
import { brand } from "@/brand";

import type { Access, Payment, PayTo, PlanDue } from "../account/platform";
import { PayOnline } from "./PayOnline";
import {
  clientStatus,
  METHOD_OPTIONS,
  planStatus,
  startWithToken,
  submitWithToken,
  todayIST,
  upiLink,
  type InactiveDetail,
  type PaymentBody,
} from "./lib";

function qrDataUrl(text: string): string {
  const qr = qrcode(0, "M");
  qr.addData(text);
  qr.make();
  return qr.createDataURL(4, 8);
}

function CopyValue({ value }: { value: string }) {
  const [done, setDone] = useState(false);
  return (
    <button
      type="button"
      className="inline-flex size-7 shrink-0 items-center justify-center rounded-md text-subtle hover:bg-surface-2 hover:text-fg"
      aria-label={`Copy ${value}`}
      onClick={() => {
        void navigator.clipboard?.writeText(value).then(() => {
          setDone(true);
          setTimeout(() => setDone(false), 1500);
        });
      }}
    >
      {done ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
    </button>
  );
}

/** Price (GST included) with its taxable value and GST. */
/** The plan's state in one badge: "Trial · ends 22 Nov 2026", "Paid · until …",
 * "Payment pending", "Ended" (from the stored subscription kind and period). */
export function PlanStatusBadge({ access }: { access: Access }) {
  if (access.reason === "admin") return <Badge tone="neutral">Admin</Badge>;
  if (access.kind === "ra_client") {
    const c = clientStatus(access.ra_name, access.reason, access.grace_until);
    return (
      <Badge tone={c.tone} className="whitespace-nowrap">
        {c.label}
      </Badge>
    );
  }
  const kind = access.kind === "none" ? (access.reason === "payment_pending" ? "pending" : null) : access.kind;
  const s = planStatus(kind, access.until, access.active);
  return (
    <Badge tone={s.tone} className="whitespace-nowrap">
      {s.label}
    </Badge>
  );
}

/** The launch offer on the amount due (V064): label, struck regular price, months left. */
export function OfferLine({ plan }: { plan: PlanDue }) {
  if (!plan.promo_label || !plan.regular_price) return null;
  const left = plan.promo_months_left ?? 0;
  return (
    <div className="grid gap-0.5 rounded-md bg-brand-soft px-2.5 py-2 text-xs text-fg">
      <span className="font-semibold text-brand">{plan.promo_label}</span>
      <span>
        <span className="num font-semibold">{formatINR(plan.price_monthly, 0)}</span>/month instead of{" "}
        <s className="num text-muted">{formatINR(plan.regular_price, 0)}</s>
        {left > 0 ? ` · ${left} offer month${left === 1 ? "" : "s"} left` : ""}
      </span>
    </div>
  );
}

export function AmountDue({ plan }: { plan: PlanDue }) {
  return (
    <div className="grid gap-1 rounded-lg border border-border p-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="text-sm font-medium text-fg">{plan.name}, 1 month</span>
        <span className="flex items-baseline gap-2">
          {plan.promo_label && plan.regular_price ? (
            <s className="num text-sm text-muted">{formatINR(plan.regular_price)}</s>
          ) : null}
          <span className="num text-lg font-semibold text-fg">{formatINR(plan.total_amount)}</span>
        </span>
      </div>
      <OfferLine plan={plan} />
      <dl className="grid grid-cols-[1fr_auto] gap-x-3 gap-y-0.5 text-xs text-muted">
        <dt>Taxable value</dt>
        <dd className="num text-right">{formatINR(plan.base_amount)}</dd>
        <dt>GST @ {plan.gst_rate}%</dt>
        <dd className="num text-right">{formatINR(plan.gst_amount)}</dd>
        <dt className="font-medium text-fg">Total payable (GST included)</dt>
        <dd className="num text-right font-medium text-fg">{formatINR(plan.total_amount)}</dd>
      </dl>
    </div>
  );
}

const BANK_ROWS: { key: keyof PayTo; label: string }[] = [
  { key: "account_name", label: "Account name" },
  { key: "account_number", label: "Account number" },
  { key: "ifsc", label: "IFSC" },
  { key: "bank_name", label: "Bank" },
  { key: "branch", label: "Branch" },
  { key: "account_type", label: "Account type" },
];

/** Bank-transfer details, plus UPI (QR and app button) when a UPI ID is set. */
export function PayToDetails({ payTo, plan, note }: { payTo: PayTo; plan: PlanDue; note: string }) {
  const rows = BANK_ROWS.filter((r) => payTo[r.key]);
  const upi = payTo.upi_id ? upiLink(payTo, plan, note) : null;
  const qr = useMemo(() => (upi ? qrDataUrl(upi) : null), [upi]);
  return (
    <div className="grid gap-3">
      <div className="grid gap-1.5">
        <p className="text-xs font-medium text-muted">Pay by bank transfer (NEFT / IMPS / RTGS)</p>
        {rows.length ? (
          <dl className="grid gap-1 rounded-lg bg-surface-2 p-3 text-sm">
            {rows.map((r) => (
              <div key={r.key} className="grid grid-cols-[7.5rem_minmax(0,1fr)_auto] items-center gap-2">
                <dt className="text-xs text-muted">{r.label}</dt>
                <dd className="num min-w-0 break-words font-medium text-fg">{payTo[r.key]}</dd>
                <CopyValue value={payTo[r.key]} />
              </div>
            ))}
          </dl>
        ) : (
          <p className="rounded-lg bg-warn-soft px-3 py-2 text-sm text-fg">
            Payment details are being set up. Write to {brand.supportEmail} and we'll send them.
          </p>
        )}
      </div>
      {upi && qr ? (
        <div className="grid gap-2 rounded-lg border border-border p-3 sm:grid-cols-[auto_1fr] sm:items-center">
          <img src={qr} alt="UPI QR code" className="size-36 rounded-md bg-white p-1" />
          <div className="grid gap-2">
            <p className="text-xs font-medium text-muted">Or pay by UPI</p>
            <p className="text-sm break-words text-fg">
              {payTo.payee_name ? `${payTo.payee_name} · ` : ""}
              <span className="num">{payTo.upi_id}</span>
            </p>
            <a
              href={upi}
              className="inline-flex h-9 w-fit items-center rounded-lg bg-brand px-3 text-sm font-medium text-white hover:opacity-90"
            >
              Pay {formatINR(plan.total_amount)} by UPI
            </a>
            <p className="text-2xs text-subtle">Scan with any UPI app, or tap the button on your phone.</p>
          </div>
        </div>
      ) : null}
      {payTo.instructions ? <p className="text-xs whitespace-pre-line text-muted">{payTo.instructions}</p> : null}
    </div>
  );
}

/** "I've paid": the transaction reference, amount and date. */
export function PaymentForm({
  plan,
  submit,
  onDone,
}: {
  plan: PlanDue;
  submit: (body: PaymentBody) => Promise<Payment>;
  onDone?: (p: Payment) => void;
}) {
  const [reference, setReference] = useState("");
  const [amount, setAmount] = useState(String(plan.total_amount));
  const [paidOn, setPaidOn] = useState(todayIST());
  const [method, setMethod] = useState("bank");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState<Payment | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const ref = reference.trim();
    if (ref.length < 4) return setError("Enter the UTR / transaction reference from your bank or UPI app.");
    const amt = Number(amount);
    if (!(amt > 0)) return setError("Enter the amount you paid.");
    setBusy(true);
    try {
      const p = await submit({ plan_code: plan.code, amount: amt, reference: ref, paid_on: paidOn, method });
      // The plan's price, not what was typed: a funnel count, not a ledger.
      track("payment_submitted", { plan: plan.code, value: plan.total_amount, currency: "INR", method });
      setSent(p);
      onDone?.(p);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't send the payment details.");
    } finally {
      setBusy(false);
    }
  }

  if (sent)
    return (
      <p role="status" className="rounded-lg bg-up-soft px-3 py-2 text-sm text-fg">
        <span className="font-medium">Thank you.</span> We received your payment reference{" "}
        <span className="num font-medium">{sent.reference}</span> and will activate your account
        within one working day. You'll get an email when it's done.
      </p>
    );

  return (
    <form onSubmit={onSubmit} className="grid gap-3" noValidate aria-label="Payment details">
      <p className="text-xs font-medium text-muted">After paying, tell us the transaction reference</p>
      <div className="grid gap-3 md:grid-cols-2">
        <Field label="UTR / transaction reference" htmlFor="pay-ref">
          <Input
            id="pay-ref"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            placeholder="e.g. NEFT or UPI reference"
            autoComplete="off"
          />
        </Field>
        <Field label="Paid by" htmlFor="pay-method">
          <div id="pay-method" className="min-w-0">
            <Select label="Paid by" value={method} onValueChange={setMethod} options={METHOD_OPTIONS} className="h-10 w-full" />
          </div>
        </Field>
        <Field label="Amount paid (₹)" htmlFor="pay-amount">
          <Input
            id="pay-amount"
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ""))}
          />
        </Field>
        <Field label="Date paid" htmlFor="pay-date">
          <Input id="pay-date" type="date" value={paidOn} max={todayIST()} onChange={(e) => setPaidOn(e.target.value)} />
        </Field>
      </div>
      {error ? (
        <p role="alert" className="rounded-lg bg-down-soft px-3 py-2 text-sm text-down">
          {error}
        </p>
      ) : null}
      <Button type="submit" loading={busy} className="w-full sm:w-fit">
        Send payment details
      </Button>
    </form>
  );
}

/** Neither online payment nor bank transfer is on: a calm note with the support address. */
export function PayUnavailable() {
  return (
    <div role="status" className="grid gap-1 rounded-lg border border-border bg-surface-2 p-3">
      <p className="text-sm font-semibold text-fg">Online payment is temporarily unavailable</p>
      <p className="text-sm break-words text-muted">
        Please try again a little later, or write to{" "}
        <a href={`mailto:${brand.supportEmail}`} className="font-medium text-brand hover:underline">
          {brand.supportEmail}
        </a>{" "}
        and we'll help you pay.
      </p>
    </div>
  );
}

/** The whole "pay to activate" block for a signed-out person. */
export function ActivatePayment({ detail, email }: { detail: InactiveDetail; email: string }) {
  if (!detail.plan) return <p className="text-sm text-muted">{detail.message}</p>;
  const bank = detail.bank_transfer === true;
  return (
    <div className="grid gap-4">
      <AmountDue plan={detail.plan} />
      {detail.online ? (
        <>
          <PayOnline plan={detail.plan} start={(b) => startWithToken(detail.payment_token, b)} />
          {bank ? <p className="text-xs font-medium text-muted">Or pay by bank transfer and send us the reference</p> : null}
        </>
      ) : bank ? null : (
        <PayUnavailable />
      )}
      {bank ? (
        <>
          <PayToDetails payTo={detail.pay_to} plan={detail.plan} note={`${detail.plan.name} ${email}`} />
          <PaymentForm plan={detail.plan} submit={(b) => submitWithToken(detail.payment_token, b)} />
        </>
      ) : null}
    </div>
  );
}
